import { Caption } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import Button from "components/button/Button";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import ExportIcon from "components/icons/ExportIcon";
import CircleNumber from "components/products/CircleWithNumber";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import { LicenseData } from "domain/licenses";
import { BarOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { SoftwarePackage } from "domain/softwarePackages";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const UBUNTU_UPDATE_COMMAND = "sudo apt update && sudo apt upgrade -y";
const CHMOD_SCRIPT_COMMAND = "sudo chmod +x /path/to/script/0-run_me.sh";
const RUN_SCRIPT_COMMAND = "sudo /path/to/script/0-run_me.sh";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepIndex: number;
    menuExpanded: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
    fetchedSoftwarePackages?: SoftwarePackage[];
    fetchedLicenses: LicenseData[];
}

interface StepAction {
    label: string;
    assetImageLabel?: string;
    onClick: () => void;
}

interface Step {
    description: React.ReactNode;
    actions?: StepAction[];
}

const SetupServerConfiguration = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [showContent, setShowContent] = React.useState<boolean>(false);
    const stepName = BarOnboardingSteps.STEP_SETUP_SERVER_CONFIGURATION;

    const clickEvent = () => {
        props.stepClickable && setShowContent(!showContent);
    };

    const saveOnboardingStep = () => {
        props.saveOnboardingStep({
            product: OnboardingProductType.BAR,
            step: BarOnboardingSteps.STEP_SETUP_SERVER_CONFIGURATION,
        });
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const theme = useTheme();

    const configurationTool = props.fetchedSoftwarePackages?.find(
        (pkg) => pkg.productName === "Blancco Drive Eraser Configuration Tool"
    );

    const getBdePackage = () => {
        const userLicenseIds = props.fetchedLicenses?.map((license) => license.type.toString()) || [];

        if (userLicenseIds.includes("20")) {
            return props.fetchedSoftwarePackages?.find((pkg) => pkg.licenseIds.includes("20"));
        } else if (userLicenseIds.includes("36")) {
            return props.fetchedSoftwarePackages?.find((pkg) => pkg.licenseIds.includes("36"));
        } else if (userLicenseIds.includes("50")) {
            return props.fetchedSoftwarePackages?.find((pkg) => pkg.licenseIds.includes("50"));
        }
        return undefined;
    };

    const driveEraser = getBdePackage();
    const blanccoAssetReimaging = props.fetchedSoftwarePackages?.find(
        (pkg) => pkg.productName === "Blancco Asset Reimaging"
    );

    const configurationToolUrl = configurationTool?.versions?.[0]?.downloadUrl;
    const driveEraserIso = driveEraser?.versions?.[0]?.downloadUrl;
    const blanccoAssetReimagingZip = blanccoAssetReimaging?.versions?.[0]?.downloadUrl;

    const handleDownload = (url?: string) => {
        if (url) {
            const link = document.createElement("a");
            link.href = url;
            link.download = "";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const configurationSteps: Step[] = [
        {
            description: (
                <Trans
                    i18nKey={t("Onboarding.barHomePage.setupServerConfiguration.steps.ubuntuScript")}
                    components={{
                        commandOne: <Caption bolded>{UBUNTU_UPDATE_COMMAND}</Caption>,
                        commandTwo: <Caption bolded>{CHMOD_SCRIPT_COMMAND}</Caption>,
                        commandThree: <Caption bolded>{RUN_SCRIPT_COMMAND}</Caption>,
                    }}
                />
            ),
            actions: [
                {
                    label: t("Onboarding.barHomePage.setupServerConfiguration.downloads.linuxScriptFiles"),
                    onClick: () =>
                        handleDownload(
                            "https://download.blancco.com/products/asset_reimaging/scripts/pxe/scripts/linux/0-run_me.sh"
                        ),
                },
            ],
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.windowsDownload"),
            actions: [
                {
                    label: t("Onboarding.barHomePage.setupServerConfiguration.downloads.isoFile"),
                    onClick: () => handleDownload(driveEraserIso),
                },
                {
                    label: t("Onboarding.barHomePage.setupServerConfiguration.downloads.configurationTool"),
                    onClick: () => handleDownload(configurationToolUrl),
                },
            ],
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.installTool"),
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.communications"),
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.saveConfiguration"),
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.downloadWinBuilder"),
            actions: [
                {
                    label: t("Onboarding.barHomePage.setupServerConfiguration.downloads.winBuilder"),
                    assetImageLabel: t("Onboarding.barHomePage.setupServerConfiguration.downloads.assetImage"),
                    onClick: () => handleDownload(blanccoAssetReimagingZip),
                },
            ],
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.openAssetReimaging"),
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.enterUrl"),
            actions: [
                {
                    label: "https://classic.eu-west-1.blancco.cloud",
                    onClick: () => {},
                },
            ],
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.enterCredentials"),
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.enterImagePath"),
            actions: [
                {
                    label: "\\\\10.0.1.1\\images",
                    onClick: () => {},
                },
            ],
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.selectOperatingMode"),
        },
        {
            description: t("Onboarding.barHomePage.setupServerConfiguration.steps.selectMediaType"),
        },
    ];

    function createContent() {
        return (
            <>
                <div className={style.stepsContainer}>
                    {configurationSteps.map((step, index) => (
                        <div key={index}>
                            <div className={style.subStepsToFollowLayout}>
                                <CircleNumber number={index + 1} subStep={true} />
                                <div className={style.stepPadding}>
                                    <div>{step.description}</div>
                                    {step.actions && step.actions.length > 0 && (
                                        <div className={style.columnLayout}>
                                            {step.actions.map((action, actionIndex) => {
                                                const isCopyAction =
                                                    action.label.includes("https://") || action.label.includes("\\");
                                                return (
                                                    <div key={actionIndex}>
                                                        {isCopyAction ? (
                                                            <div
                                                                className={classNames(
                                                                    style.stepActionButton,
                                                                    style.linuxCommandFont
                                                                )}
                                                            >
                                                                <div>{action.label}</div>
                                                                <div className={style.stepActionButtonWrapper}>
                                                                    <div className={style.actionButton}>
                                                                        <CopyToClipboard value={action.label} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={style.stepActionButton}>
                                                                <div>
                                                                    <label>{action.label}</label>
                                                                    <br />
                                                                    <label>{action.assetImageLabel}</label>
                                                                </div>
                                                                <div className={style.stepActionButtonWrapper}>
                                                                    <div className={style.actionButton}>
                                                                        <Button
                                                                            variant={"TERTIARY"}
                                                                            onClick={action.onClick}
                                                                            disabled={
                                                                                !configurationToolUrl &&
                                                                                action.onClick
                                                                                    .toString()
                                                                                    .includes("configurationToolUrl")
                                                                            }
                                                                        >
                                                                            <ExportIcon
                                                                                size={24}
                                                                                color={theme.iconFillColor}
                                                                            />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <div className={style.templateSelectedBlock}>
                            <StepCompletedText
                                successText={t("Onboarding.gettingStartedFlow.completed")}
                                testId={testIds.workArea.onboarding.step.statusLabel}
                                dataType={OnboardingProductType.BAR + "_" + stepName}
                            />
                        </div>
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            onClick={saveOnboardingStep}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={OnboardingProductType.BAR + "_" + stepName}
                        >
                            {t("Onboarding.gettingStartedFlow.markAsCompleted")}
                        </Button>
                    )}
                </div>
            </>
        );
    }

    return (
        <StepHeader
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            title={t("Onboarding.barHomePage.setupServerConfiguration.title")}
            stepIndex={props.stepIndex}
            menuExpanded={props.menuExpanded}
            showContent={showContent}
            clickEvent={clickEvent}
            content={createContent()}
            type={OnboardingProductType.BAR}
            stepName={stepName}
        />
    );
};

export default connector(SetupServerConfiguration);
